import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { SITE } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PaymentSuccessPage — post Razorpay redirect landing.
 * Polls /api/razorpay/status/:order_id until payment_status is 'paid' (or times out).
 */
export default function PaymentSuccessPage() {
    const [params] = useSearchParams();
    const orderId = params.get("order_id");
    const flag = params.get("status"); // 'verify_failed' when signature failed
    const [state, setState] = useState(
        flag === "verify_failed" ? "error" : "pending",
    );
    const [statusData, setStatusData] = useState(null);
    const attempts = useRef(0);
    const MAX_ATTEMPTS = 8;
    const POLL_MS = 1500;

    const poll = useCallback(async () => {
        if (!orderId) {
            setState("error");
            return;
        }
        try {
            const { data } = await axios.get(
                `${API}/razorpay/status/${orderId}`,
            );
            setStatusData(data);
            if (data.payment_status === "paid") {
                setState("paid");
                return;
            }
            if (
                data.payment_status === "signature_failed" ||
                data.payment_status === "failed"
            ) {
                setState("failed");
                return;
            }
            attempts.current += 1;
            if (attempts.current >= MAX_ATTEMPTS) {
                setState("timeout");
                return;
            }
            setTimeout(poll, POLL_MS);
        } catch (err) {
            setState("error");
        }
    }, [orderId]);

    useEffect(() => {
        if (state === "pending") poll();
    }, [poll, state]);

    return (
        <div className="pt-24 min-h-[80vh]" data-testid="payment-success-page">
            <section className="max-w-3xl mx-auto px-6 lg:px-12 py-20 lg:py-28 text-center">
                {state === "pending" && (
                    <div data-testid="pay-state-pending">
                        <Loader2
                            className="h-10 w-10 text-[#A68A6B] animate-spin mx-auto"
                            strokeWidth={1.25}
                        />
                        <h1 className="mt-8 font-serif font-light tracking-tight leading-[1.05] text-4xl sm:text-5xl text-[#2C2B29]">
                            Confirming your payment…
                        </h1>
                        <p className="mt-4 text-base text-[#4A4947]">
                            One quiet moment. This usually takes a few seconds.
                        </p>
                    </div>
                )}

                {state === "paid" && (
                    <div data-testid="pay-state-paid">
                        <CheckCircle2
                            className="h-12 w-12 text-[#8A9A86] mx-auto"
                            strokeWidth={1.25}
                        />
                        <p className="ssh-eyebrow mt-6">You&apos;re in</p>
                        <h1 className="mt-4 font-serif font-light tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-[#2C2B29]">
                            Thank you.{" "}
                            <em className="italic text-[#A68A6B]">
                                Your session is booked.
                            </em>
                        </h1>
                        <p className="mt-6 text-lg leading-relaxed text-[#4A4947] max-w-xl mx-auto">
                            A confirmation email is on its way to you, and
                            Shreya has been notified. She'll share your session
                            link and any gentle next steps before your
                            appointment.
                        </p>
                        <div className="mt-10 bg-[#FAF8F5] border border-[#EFEBE4] p-6 text-left text-sm text-[#4A4947]">
                            <p className="ssh-eyebrow">Your appointment</p>
                            {statusData?.program_name && (
                                <p className="mt-3">
                                    <strong>Session:</strong>{" "}
                                    {statusData.program_name}
                                </p>
                            )}
                            {statusData?.booking_date && (
                                <p className="mt-2" data-testid="pay-booking-slot">
                                    <strong>When:</strong>{" "}
                                    {statusData.booking_date}
                                    {statusData.booking_time
                                        ? ` at ${statusData.booking_time} IST`
                                        : ""}
                                </p>
                            )}
                            <p className="mt-2">
                                <strong>Order ID:</strong>{" "}
                                <span className="font-mono text-xs break-all">
                                    {orderId}
                                </span>
                            </p>
                            {statusData?.amount_paise ? (
                                <p className="mt-2">
                                    <strong>Amount:</strong>{" "}
                                    {(
                                        statusData.amount_paise / 100
                                    ).toLocaleString("en-IN")}{" "}
                                    {statusData.currency}
                                </p>
                            ) : null}
                        </div>
                        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                            <Link to="/">
                                <Button
                                    variant="outline"
                                    className="rounded-none border-[#2C2B29] bg-transparent text-[#2C2B29] hover:bg-[#2C2B29] hover:text-white px-9 py-7 text-xs uppercase tracking-[0.22em] transition-colors"
                                    data-testid="pay-back-home"
                                >
                                    Return Home
                                </Button>
                            </Link>
                            <a
                                href={`https://wa.me/${SITE.whatsappRaw}?text=${encodeURIComponent(
                                    "Hi Shreya, I just enrolled — looking forward to beginning.",
                                )}`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <Button
                                    className="ssh-btn-primary rounded-none px-9 py-7 text-xs uppercase tracking-[0.22em] inline-flex items-center gap-2"
                                    data-testid="pay-msg-shreya"
                                >
                                    Message Shreya
                                    <ArrowRight
                                        className="h-4 w-4"
                                        strokeWidth={1.5}
                                    />
                                </Button>
                            </a>
                        </div>
                    </div>
                )}

                {(state === "failed" ||
                    state === "error" ||
                    state === "timeout") && (
                    <div data-testid="pay-state-failed">
                        <XCircle
                            className="h-12 w-12 text-[#A68A6B] mx-auto"
                            strokeWidth={1.25}
                        />
                        <h1 className="mt-8 font-serif font-light tracking-tight leading-[1.05] text-4xl sm:text-5xl text-[#2C2B29]">
                            {state === "failed"
                                ? "This payment could not be verified."
                                : state === "timeout"
                                  ? "We couldn't confirm payment in time."
                                  : "Something went quiet on our end."}
                        </h1>
                        <p className="mt-4 text-base text-[#4A4947] max-w-xl mx-auto">
                            If money was deducted, please WhatsApp Shreya with
                            your Order ID and it will be resolved immediately.
                        </p>
                        {orderId && (
                            <p className="mt-3 text-xs text-[#75736F] font-mono">
                                Order: {orderId}
                            </p>
                        )}
                        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                            <Link to="/packages">
                                <Button
                                    className="ssh-btn-primary rounded-none px-9 py-7 text-xs uppercase tracking-[0.22em]"
                                    data-testid="pay-try-again"
                                >
                                    Back to programs
                                </Button>
                            </Link>
                            <a
                                href={`https://wa.me/${SITE.whatsappRaw}?text=${encodeURIComponent(
                                    `Hi Shreya, I paid but got an error. My order ID is ${orderId || "unknown"}`,
                                )}`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <Button
                                    variant="outline"
                                    className="rounded-none border-[#2C2B29] bg-transparent text-[#2C2B29] hover:bg-[#2C2B29] hover:text-white px-9 py-7 text-xs uppercase tracking-[0.22em] transition-colors"
                                >
                                    WhatsApp Shreya
                                </Button>
                            </a>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}
